/**
 * Owner: Masa Bayu
 * File: qx-draft-commands.ts
 * Date:Time: 2026-10-16 06:34
 * Description: Draft format — Gap (sibling margin) · align · dropcap.
 */

import {
  insertDraftImageFigure,
  runDraftFigureDelete,
  runDraftFigureFloat,
  runDraftFigureFrame,
  runDraftFigurePreset,
  runDraftFigureShadow,
} from './qx-draft-image';
import {
  captureDraftDropcapTarget,
  reassertDraftDropcapLetterFonts,
  runDraftDropcap,
  runDraftDropcapFont,
} from './qx-draft-dropcap';
import { runDraftFigureNote } from './qx-draft-image-note';
export type {
  DraftFigureFloat,
  DraftFigureFrame,
  DraftFigurePreset,
  DraftFigureShadow,
} from './qx-draft-image';
export {
  runDraftFigureDelete,
  runDraftFigureFloat,
  runDraftFigureFrame,
  runDraftFigurePreset,
  runDraftFigureShadow,
  captureDraftDropcapTarget,
  runDraftDropcap,
  runDraftDropcapFont,
  runDraftFigureNote,
};

export type DraftFormatCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikeThrough'
  | 'superscript'
  | 'subscript'
  | 'justifyLeft'
  | 'justifyCenter'
  | 'justifyRight'
  | 'justifyFull'
  | 'insertUnorderedList'
  | 'insertOrderedList'
  | 'indent'
  | 'outdent'
  | 'removeFormat'
  | 'undo'
  | 'redo';

export type DraftBlockCommand = 'p' | 'h1' | 'h2';

/**
 * lib.dom marks Document.execCommand @deprecated. TipTap is HOLD, so contentEditable
 * still needs the legacy editing host — call it only through this untyped bridge.
 */
type LegacyEditHost = {
  execCommand(commandId: string, showUI?: boolean, value?: string): boolean;
  queryCommandState(commandId: string): boolean;
};

function legacyEdit(): LegacyEditHost {
  return document as unknown as LegacyEditHost;
}

function runLegacyEdit(commandId: string, value?: string): boolean {
  try {
    return value === undefined
      ? legacyEdit().execCommand(commandId, false)
      : legacyEdit().execCommand(commandId, false, value);
  } catch {
    return false;
  }
}

const BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, div';

function closestBlock(node: Node | null): HTMLElement | null {
  let current: Node | null = node;
  while (current && current !== document.body) {
    if (current instanceof HTMLElement && current.matches(BLOCK_SELECTOR)) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

/** Replace the current block tag via Selection/Range (no deprecated formatBlock). */
export function runDraftBlock(block: DraftBlockCommand): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  const blockEl = closestBlock(range.commonAncestorContainer);
  if (!blockEl || blockEl.isContentEditable === false) {
    // Fallback: wrap selection in the requested block.
    try {
      const wrapper = document.createElement(block);
      range.surroundContents(wrapper);
      selection.removeAllRanges();
      const next = document.createRange();
      next.selectNodeContents(wrapper);
      next.collapse(false);
      selection.addRange(next);
      return true;
    } catch {
      return runLegacyEdit('formatBlock', block);
    }
  }

  if (blockEl.tagName.toLowerCase() === block) return true;

  const replacement = document.createElement(block);
  replacement.innerHTML = blockEl.innerHTML;
  for (const attr of Array.from(blockEl.attributes)) {
    replacement.setAttribute(attr.name, attr.value);
  }
  blockEl.replaceWith(replacement);

  const next = document.createRange();
  next.selectNodeContents(replacement);
  next.collapse(false);
  selection.removeAllRanges();
  selection.addRange(next);
  return true;
}

/** First-line indent step (pt) · maps to tip format.firstLineIndent. */
export const DRAFT_INDENT_STEP_PT = 36;
const DRAFT_INDENT_MAX_PT = DRAFT_INDENT_STEP_PT * 8;

function parseStyleLengthPt(raw: string): number {
  const value = raw.trim().toLowerCase();
  if (!value) return 0;
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (value.endsWith('pt')) return Math.round(n);
  if (value.endsWith('px')) return Math.round(n * 0.75);
  if (value.endsWith('em')) return Math.round(n * 12);
  return Math.round(n);
}

/** Read text-indent as pt (first line only · ≠ whole-block margin). */
export function readDraftIndentPt(el: HTMLElement): number {
  return parseStyleLengthPt(el.style.textIndent || '');
}

/**
 * Tap Indent → gerak ke kanan pada awal perenggan sahaja (baris pertama).
 * List items keep legacy nest indent; body uses text-indent pt.
 */
export function runDraftBlockIndent(deltaSteps: number): boolean {
  if (deltaSteps === 0) return false;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const block = closestBlock(selection.getRangeAt(0).commonAncestorContainer);
  if (!block || block.isContentEditable === false) return false;

  if (block.tagName === 'LI') {
    return runLegacyEdit(deltaSteps > 0 ? 'indent' : 'outdent');
  }

  const current = readDraftIndentPt(block);
  const next = Math.min(
    DRAFT_INDENT_MAX_PT,
    Math.max(0, current + deltaSteps * DRAFT_INDENT_STEP_PT),
  );
  if (next === current) return false;
  // Drop mistaken whole-block indent from earlier Indent slice.
  block.style.marginLeft = '';
  if (next <= 0) block.style.textIndent = '';
  else block.style.textIndent = `${next}pt`;
  return true;
}

export type DraftTextAlign = 'left' | 'center' | 'right' | 'justify';

const ALIGN_BY_COMMAND: Partial<Record<DraftFormatCommand, DraftTextAlign>> = {
  justifyLeft: 'left',
  justifyCenter: 'center',
  justifyRight: 'right',
  justifyFull: 'justify',
};

function blocksIntersectingSelection(editor: HTMLElement): HTMLElement[] {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return [];
  const range = selection.getRangeAt(0);
  const candidates = Array.from(
    editor.querySelectorAll(':scope > p, :scope > div, p'),
  ).filter((el): el is HTMLElement => el instanceof HTMLElement);
  if (selection.isCollapsed) {
    const block = closestBlock(range.commonAncestorContainer);
    return block && editor.contains(block) ? [block] : [];
  }
  return candidates.filter((el) => {
    try {
      return range.intersectsNode(el);
    } catch {
      return false;
    }
  });
}

/**
 * Apply text-align to every selected body block (not flaky execCommand).
 * Multi-paragraph selection → all intersecting paragraphs.
 */
export function runDraftAlign(
  align: DraftTextAlign,
  root?: HTMLElement | null,
): boolean {
  const editor = editorRoot(root);
  if (!editor) return false;
  let blocks = blocksIntersectingSelection(editor);
  if (blocks.length === 0) {
    const current = currentParagraphBlock();
    if (current && editor.contains(current)) blocks = [current];
  }
  if (blocks.length === 0) return false;
  for (const el of blocks) {
    el.style.textAlign = align;
  }
  return true;
}

/** After Enter · copy previous sibling align onto the new block. */
export function inheritDraftBlockAlign(root?: HTMLElement | null): void {
  const editor = editorRoot(root);
  if (!editor) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const block = closestBlock(selection.focusNode);
  if (!block || !editor.contains(block) || isBlankBlock(block)) return;
  if (block.style.textAlign) return;
  let prev = block.previousElementSibling;
  while (prev instanceof HTMLElement && isBlankBlock(prev)) {
    prev = prev.previousElementSibling;
  }
  if (!(prev instanceof HTMLElement)) return;
  const from = prev.style.textAlign.trim().toLowerCase();
  if (from === 'left' || from === 'center' || from === 'right' || from === 'justify') {
    block.style.textAlign = from;
  }
}

/** Run a native editing command against the focused draft surface. */
export function runDraftFormat(command: DraftFormatCommand): boolean {
  if (command === 'indent') return runDraftBlockIndent(1);
  if (command === 'outdent') return runDraftBlockIndent(-1);
  const align = ALIGN_BY_COMMAND[command];
  if (align) return runDraftAlign(align);
  return runLegacyEdit(command);
}

/** Gap between paragraphs (pt) · Word-like Space After document style. */
export const DRAFT_PARAGRAPH_GAP_PT = [0, 2, 4, 6, 8, 10, 12, 18, 24] as const;

/** @deprecated use DRAFT_PARAGRAPH_GAP_PT */
export const DRAFT_PARAGRAPH_SPACE_PT = DRAFT_PARAGRAPH_GAP_PT;

export type DraftParagraphSpaceKind = 'before' | 'after';

function editorRoot(root?: HTMLElement | null): HTMLElement | null {
  if (root) return root;
  const node = document.querySelector('.qxw-page__editor');
  return node instanceof HTMLElement ? node : null;
}

/** Enter spacer only · image/figure blocks keep document gap. */
function isBlankBlock(el: HTMLElement): boolean {
  if (el.querySelector('img, figure, video, table, .qxw-draft-figure')) return false;
  const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  if (text.length > 0) return false;
  const elements = Array.from(el.children);
  if (elements.length === 0) return true;
  return elements.length === 1 && elements[0] instanceof HTMLBRElement;
}

function currentParagraphBlock(): HTMLElement | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const block = closestBlock(selection.getRangeAt(0).commonAncestorContainer);
  if (!block || block.isContentEditable === false) return null;
  if (block.tagName === 'LI') return null;
  return block;
}

function isFloatFigure(el: Element | null): boolean {
  return (
    el instanceof HTMLElement &&
    el.tagName === 'FIGURE' &&
    (el.classList.contains('qxw-draft-figure--float-left') ||
      el.classList.contains('qxw-draft-figure--float-right'))
  );
}

function isGapBodyBlock(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'P' || tag === 'DIV' || tag === 'H1' || tag === 'H2' || tag === 'H3';
}

/**
 * Set paragraph gap for the whole draft.
 * Gap = margin-top on each content block after another content block.
 * (padding/margin-bottom collapse beside floats — sibling margin-top does not.)
 * First wrap after a float stays flush to the image top.
 */
export function runDraftParagraphGap(pt: number, root?: HTMLElement | null): boolean {
  if (!Number.isFinite(pt) || pt < 0) return false;
  const editor = editorRoot(root);
  if (!editor) return false;
  const gap = Math.round(pt);
  const value = `${gap}pt`;
  editor.style.setProperty('--qxw-para-gap', value);
  editor.setAttribute('data-qxw-para-gap', String(gap));
  const children = Array.from(editor.children);
  for (const el of children) {
    if (!isGapBodyBlock(el)) continue;
    // Clear legacy Gap carriers from earlier approaches.
    el.style.removeProperty('padding-bottom');
    el.style.removeProperty('margin-bottom');
    if (el.dataset.qxwGapTop === '1') {
      el.style.removeProperty('margin-top');
      delete el.dataset.qxwGapTop;
    }
    if (gap <= 0 || isBlankBlock(el)) continue;
    const prev = el.previousElementSibling;
    // Flush wrap text under float top — no Gap above first wrap paragraph.
    if (isFloatFigure(prev)) continue;
    if (!isGapBodyBlock(prev) || isBlankBlock(prev)) continue;
    el.style.marginTop = value;
    el.dataset.qxwGapTop = '1';
  }
  return true;
}

/** Read document gap from editor data attribute / CSS var. */
export function readDraftParagraphGapPt(root?: HTMLElement | null): number {
  const editor = editorRoot(root);
  if (!editor) return 0;
  const attr = editor.getAttribute('data-qxw-para-gap');
  if (attr !== null && attr !== '') {
    const n = Number(attr);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const css = editor.style.getPropertyValue('--qxw-para-gap');
  if (css.trim()) return parseStyleLengthPt(css);
  return 0;
}

/** Apply Space Before / After on the current paragraph only (pt). */
export function runDraftParagraphSpacing(
  kind: DraftParagraphSpaceKind,
  pt: number,
): boolean {
  if (!Number.isFinite(pt) || pt < 0) return false;
  const block = currentParagraphBlock();
  if (!block) return false;
  const value = `${Math.round(pt)}pt`;
  if (kind === 'before') block.style.marginTop = value;
  else block.style.marginBottom = value;
  return true;
}

/** Read current Space Before / After (pt) from the selected paragraph. */
export function readDraftParagraphSpacingPt(
  kind: DraftParagraphSpaceKind,
  el?: HTMLElement | null,
): number | null {
  const block = el ?? currentParagraphBlock();
  if (!block) return null;
  const raw = kind === 'before' ? block.style.marginTop : block.style.marginBottom;
  if (!raw.trim()) return null;
  return parseStyleLengthPt(raw);
}

export function insertDraftImage(src: string): boolean {
  return insertDraftImageFigure(src);
}

/** Apply a font family to the current draft selection / typing style. */
export function runDraftFontFamily(fontName: string): boolean {
  const name = fontName.trim();
  if (!name) return false;
  try {
    legacyEdit().execCommand('styleWithCSS', false, 'true');
  } catch {
    /* optional */
  }
  const ok = runLegacyEdit('fontName', name);
  const sel = window.getSelection();
  const anchor = sel?.anchorNode;
  const host =
    (anchor instanceof Element ? anchor : anchor?.parentElement)?.closest(
      '.qxw-page__editor',
    ) ?? document.querySelector('.qxw-page__editor');
  reassertDraftDropcapLetterFonts(host);
  return ok;
}

/** Canonical #RRGGBB → draft text color (maps toward QX mark text-color). */
export function runDraftTextColor(hex: string): boolean {
  const color = normalizeDraftHex(hex);
  if (!color) return false;
  return runLegacyEdit('foreColor', color);
}

/** Canonical #RRGGBB → draft highlight (maps toward QX mark highlight). */
export function runDraftHighlight(hex: string): boolean {
  const color = normalizeDraftHex(hex);
  if (!color) return false;
  // Prefer hiliteColor; fall back to backColor on engines that only support that.
  return runLegacyEdit('hiliteColor', color) || runLegacyEdit('backColor', color);
}

export function normalizeDraftHex(value: string): string | null {
  const raw = value.trim();
  if (!/^#[0-9A-Fa-f]{6}$/.test(raw)) return null;
  return raw.toUpperCase();
}

export function isDraftFormatActive(command: DraftFormatCommand): boolean {
  try {
    return legacyEdit().queryCommandState(command);
  } catch {
    return false;
  }
}
